import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useParams, Redirect } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import LoadingPage from '../../../../components/templates/LoadingPage/LoadingPage';
import { LOCAL_STORAGE_KEYS, APP_ERRORS } from '../../../../constants';
import useLoginStatus from '../../../../helpers/hooks/useLoginStatus';
import { useLazyGetSiteByCodeQuery, useSelectSiteMutation } from '../../../Sites/api/api';
import GenericErrorPage, { DefaultErrorPage } from '../../components/GenericErrorPage';
import { pagePaths } from '../../config';
import { useCoreTranslation } from '../../hooks/useCoreTranslation';

import { ActionType } from './SharePage.types';

const SharePage = () => {
  const [selectSite] = useSelectSiteMutation();
  const { replace } = useHistory();
  const dispatch = useDispatch();
  const { label } = useCoreTranslation(__filename);

  const { isLoggedIn } = useLoginStatus();

  const [site, setSite] = useState<{ id: string; name: string }>();
  const [action, setAction] = useState<ActionType>(ActionType.LOADING);
  const { siteCode } = useParams<{ siteCode: string }>();
  const [getSiteByCode] = useLazyGetSiteByCodeQuery();

  const goToLogin = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.SHARED_SITE_CODE, siteCode);
    setAction(ActionType.GO_TO_LOGIN);
  }, [siteCode]);

  useEffect(
    function fetchSiteByCode() {
      (async () => {
        const { data: { site } = {}, error } = await getSiteByCode({ code: siteCode });
        const { status } = (error ?? {}) as { status: number };

        if (site) {
          if (isLoggedIn) {
            setSite(site);
          } else {
            goToLogin();
          }

          return;
        }

        if (status === 401) {
          setAction(ActionType.GO_TO_LOGIN);
        } else if (status === 404) {
          setAction(ActionType.NOT_FOUND);
          return;
        } else {
          setAction(ActionType.REQUEST_ERROR);
          return;
        }
      })();
    },
    [getSiteByCode, goToLogin, siteCode, isLoggedIn]
  );

  useEffect(
    function setCurrentSite() {
      if (!site) {
        return;
      }

      (async () => {
        await selectSite({
          dispatch,
          site: {
            id: site.id,
            name: site.name,
            locationtype: 'Site',
          },
        });
        replace(pagePaths.Home);
      })();
    },
    [dispatch, replace, selectSite, site]
  );

  switch (action) {
    case ActionType.GO_TO_LOGIN:
      return (
        <Redirect
          to={{
            pathname: pagePaths.Login,
            state: { from: pagePaths.Share.replace(':siteCode', siteCode) },
          }}
        />
      );

    case ActionType.REQUEST_ERROR:
      return <DefaultErrorPage errorId={uuidv4()} />;

    case ActionType.NOT_FOUND:
      return (
        <GenericErrorPage
          withNavbar={isLoggedIn}
          title={label('Something went wrong')}
          errors={[APP_ERRORS.SITE_LINK_INVALID]}
          actions={[
            {
              label: label('Ref: Select a site'),
              action: () => replace('/sites'),
              look: 'primary',
            },
          ]}
        />
      );

    default:
      return <LoadingPage />;
  }
};

export default SharePage;
